import { placeKey } from "./city-names";

/**
 * A destination that isn't one city with its own airport: a region priced through its metro
 * ("Bay Area": flights into SFO, OAK or SJC, venues across its cities), or a town without
 * scheduled flights reached by road from the nearest airports ("Napa": fly to SFO, about an
 * hour's drive).
 */
export interface Area {
  /** Name as the estimate shows it. */
  name: string;
  kind: "region" | "drive";
  /** Cities whose venues price the hotel, the area's own name first for a drive town. */
  cities: string[];
  /** Airports to fly into, the one fares are priced to first. */
  airports: string[];
  /** ISO country and subdivision, for the place search's context ("CA", "Italy"). */
  country: string;
  region: string;
  /** For a drive town: the road from the first airport, e.g. "~1h drive". */
  drive?: string;
  /** Other names it goes by. */
  aliases?: string[];
}

const AREA_LIST: Area[] = [
  {
    name: "Bay Area",
    kind: "region",
    cities: ["San Francisco", "Oakland", "San Jose"],
    airports: ["SFO", "OAK", "SJC"],
    country: "US",
    region: "US-CA",
    aliases: ["sf bay area", "san francisco bay area"],
  },
  {
    name: "Silicon Valley",
    kind: "region",
    cities: ["San Jose", "Palo Alto", "Mountain View", "Santa Clara"],
    airports: ["SJC", "SFO"],
    country: "US",
    region: "US-CA",
  },
  {
    name: "Napa Valley",
    kind: "region",
    cities: ["Napa", "Yountville", "St. Helena"],
    airports: ["SFO", "STS", "OAK"],
    country: "US",
    region: "US-CA",
    aliases: ["wine country"],
  },
  {
    name: "Riviera Maya",
    kind: "region",
    cities: ["Cancún", "Playa del Carmen", "Tulum"],
    airports: ["CUN"],
    country: "MX",
    region: "MX-ROO",
  },
  {
    name: "Tuscany",
    kind: "region",
    cities: ["Florence", "Pisa", "Siena", "Lucca"],
    airports: ["FLR", "PSA"],
    country: "IT",
    region: "IT-52",
    aliases: ["toscana"],
  },
  {
    name: "Napa",
    kind: "drive",
    cities: ["Napa"],
    airports: ["SFO", "STS"],
    drive: "~1h drive",
    country: "US",
    region: "US-CA",
  },
  {
    name: "Sonoma",
    kind: "drive",
    cities: ["Sonoma", "Santa Rosa"],
    airports: ["STS", "SFO"],
    drive: "~45m drive",
    country: "US",
    region: "US-CA",
  },
  {
    name: "Healdsburg",
    kind: "drive",
    cities: ["Healdsburg"],
    airports: ["STS", "SFO"],
    drive: "~20m drive",
    country: "US",
    region: "US-CA",
  },
  {
    name: "Carmel",
    kind: "drive",
    cities: ["Carmel", "Carmel-by-the-Sea"],
    airports: ["MRY", "SJC"],
    drive: "~15m drive",
    country: "US",
    region: "US-CA",
    aliases: ["carmel by the sea"],
  },
  {
    name: "Big Sur",
    kind: "drive",
    cities: ["Big Sur"],
    airports: ["MRY", "SJC"],
    drive: "~1h drive",
    country: "US",
    region: "US-CA",
  },
  {
    name: "Half Moon Bay",
    kind: "drive",
    cities: ["Half Moon Bay"],
    airports: ["SFO"],
    drive: "~40m drive",
    country: "US",
    region: "US-CA",
  },
  {
    name: "Lake Tahoe",
    kind: "drive",
    cities: ["South Lake Tahoe", "Truckee", "Tahoe City"],
    airports: ["RNO"],
    drive: "~1h drive",
    country: "US",
    region: "US-CA",
    aliases: ["tahoe"],
  },
  {
    name: "Park City",
    kind: "drive",
    cities: ["Park City"],
    airports: ["SLC"],
    drive: "~40m drive",
    country: "US",
    region: "US-UT",
  },
  {
    name: "Sedona",
    kind: "drive",
    cities: ["Sedona"],
    airports: ["PHX", "FLG"],
    drive: "~2h drive",
    country: "US",
    region: "US-AZ",
  },
  {
    name: "Ojai",
    kind: "drive",
    cities: ["Ojai"],
    airports: ["LAX", "SBA"],
    drive: "~1h30m drive",
    country: "US",
    region: "US-CA",
  },
  {
    name: "Tulum",
    kind: "drive",
    cities: ["Tulum"],
    airports: ["CUN"],
    drive: "~1h45m drive",
    country: "MX",
    region: "MX-ROO",
  },
  {
    name: "Playa del Carmen",
    kind: "drive",
    cities: ["Playa del Carmen"],
    airports: ["CUN"],
    drive: "~45m drive",
    country: "MX",
    region: "MX-ROO",
  },
];

/** Every area by {@link placeKey} of its name and aliases. */
export const AREAS: ReadonlyMap<string, Area> = new Map(
  AREA_LIST.flatMap((area) => [area.name, ...(area.aliases ?? [])].map((name) => [placeKey(name), area] as const))
);

/** Every area, once each. */
export const AREA_LIST_ALL: readonly Area[] = AREA_LIST;

/**
 * The area a destination name is, if any.
 * @param city - Destination name.
 * @returns The area, or undefined for an ordinary city.
 * @throws Never.
 */
export const areaOf = (city: string): Area | undefined => AREAS.get(placeKey(city));

/** Short names for the area caption ("San Francisco" reads as "SF"). */
const SHORT: Readonly<Record<string, string>> = { "San Francisco": "SF" };

/**
 * How an area explains itself under its name: "SF, Oakland, San Jose" for a region, "via SFO ·
 * ~1h drive" for a drive town. Null for an ordinary city.
 * @param city - Destination name.
 * @returns The caption, or null.
 * @throws Never.
 */
export const areaCaption = (city: string): string | null => {
  const area = areaOf(city);
  if (!area) return null;
  if (area.kind === "region") return area.cities.map((name) => SHORT[name] ?? name).join(", ");
  return `via ${area.airports[0]} · ${area.drive}`;
};

/** Cities close enough that their people don't fly between them. */
const LOCAL_GROUPS: readonly string[][] = [
  [
    "San Francisco",
    "Oakland",
    "San Jose",
    "Bay Area",
    "Silicon Valley",
    "Palo Alto",
    "Mountain View",
    "Berkeley",
    "Santa Clara",
    "Napa",
    "Napa Valley",
    "Sonoma",
    "Healdsburg",
    "Half Moon Bay",
  ],
  ["Los Angeles", "Santa Monica", "Burbank", "Long Beach", "Pasadena", "Anaheim"],
  ["New York", "Newark", "Brooklyn", "Jersey City"],
  ["Dallas", "Fort Worth", "Arlington"],
  ["Washington", "Arlington", "Alexandria", "Bethesda"],
].map((group) => group.map(placeKey));

/**
 * Whether travelers from one city are local to a destination: the same city, the same metro
 * (San Francisco and San Jose), or one of a region's cities. Locals drive in; they get no flight.
 * @param home - Home city.
 * @param destination - Destination name.
 * @returns True when no flight is needed.
 * @throws Never.
 */
export const isLocal = (home: string, destination: string): boolean => {
  const from = placeKey(home);
  const to = placeKey(destination);
  if (from === to) return true;
  if (areaOf(destination)?.kind === "region" && areaOf(destination)!.cities.some((city) => placeKey(city) === from)) {
    return true;
  }
  return LOCAL_GROUPS.some((group) => group.includes(from) && group.includes(to));
};
